import { createSelector } from '@reduxjs/toolkit';
import type { SupportStatusCategory } from 'features/my-zone/support-requests/types';
import {
    supportLocationRequests,
    supportRequestsTypes,
    supportStatusCategories,
} from 'features/my-zone/support-requests/consts';

import { AppState } from 'store/store';

const selectOfflineReportsState = (state: AppState) => state.offlineReports;

export const selectOfflineReports = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.reports || [],
);

export const selectOfflineReportsReportDetails = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.reportDetails || {},
);

export const selectOfflineReportsSubmitStatus = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.submitStatus || null,
);

export const selectOfflineReportsSubmitIsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.submitRequestLoading || false,
);

export const selectIsSubmitLoadingPopupVisible = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.isSubmitLoadingPopupVisible || false,
);

export const isOfflineReportsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.reports === undefined || offlineReports.reports.length === 0,
);

export const isOfflineReportsDetailsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.reportDetails === null,
);

export const selectOfflineReportsConfiguration = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.configurations,
);

export const selectOfflineReportsOpenRequests = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.openRequests,
);

export const selectOfflineReportsClosedRequests = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.closedRequests,
);

export const selectOfflineReportsSharedWithMeRequests = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.sharedWithMeRequests,
);

export const selectOfflineReportsAccountRequests = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.accountRequests,
);

export const selectOfflineReportsRequestsComments = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.comments,
);

export const selectOfflineReportsRequestsCommentsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.commentsLoading,
);

export const selectOfflineReportsRequestsPostingMailMsg = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.postingMailMsg,
);

export const selectOfflineReportsRequestsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.requestsLoading,
);

export const selectOfflineReportsSharedWithMeRequestsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.sharedWithMeRequestsLoading,
);

export const selectOfflineReportsAccountRequestsLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.accountRequestsLoading,
);

export const selectOfflineReportsUserAccountInfoLoading = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.requestsUserAccountInfoLoading,
);

export const selectOfflineReportsUserAccountInfo = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.userAccountInfo || null,
);

export const selectOfflineReportsAvailableCredits = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.availableCredits,
);

export const selectOfflineReportsCreditsBalance = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.accountCreditBalance,
);

export const selectOfflineReportsUserWeeklyCreditsBalance = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.userWeeklyBalance,
);

export const selectSupportRequests = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.supportRequests,
);

export const selectSupportStatuses = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.supportStatuses,
);

export const selectSupportTypes = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.supportTypes,
);

export const selectFilteredSupportRequests = createSelector(
    selectSupportRequests,
    selectSupportStatuses,
    selectSupportTypes,
    (supportRequests, supportStatuses, supportTypes) => {
        const filteredByStatus =
            supportStatuses.length === 0 ||
            supportStatuses.length === Object.keys(supportStatusCategories).length
                ? supportRequests.filter((request) =>
                      Object.values(supportStatusCategories).flat().includes(request.status),
                  )
                : supportRequests.filter((request) => {
                      return (Object.keys(supportStatusCategories) as SupportStatusCategory[])
                          .filter((category) => supportStatuses.includes(category))
                          .some((category) =>
                              supportStatusCategories[category].includes(request.status),
                          );
                  });

        if (supportTypes.length === 0 || supportTypes.length === supportRequestsTypes.length) {
            return filteredByStatus;
        }

        return filteredByStatus.filter((request) => {
            if (supportTypes.includes('locationRequests')) {
                return supportLocationRequests.includes(request.supportCaseCategory);
            }

            if (supportTypes.includes('generalRequests')) {
                return !supportLocationRequests.includes(request.supportCaseCategory);
            }

            return false;
        });
    },
);

export const selectSupportSearchText = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.supportSearchText,
);

export const selectSupportSelectedRequest = createSelector(
    selectOfflineReportsState,
    (offlineReports) => offlineReports.supportSelectedRequest,
);

export const selectSupportPendingRequests = createSelector(
    selectSupportRequests,
    (supportRequests) => supportRequests.filter((request) => request.status === 'Pending'),
);
